import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface ErrorMessage {
  message: string | null;
  type: 'error' | 'warning' | 'info';
  timestamp: Date;
  id: string;
  autoClose?: boolean;
  duration?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ErrorService {
  private errorsSubject = new BehaviorSubject<ErrorMessage[]>([]);
  public errors$: Observable<ErrorMessage[]> = this.errorsSubject.asObservable();
  
  constructor() { }
  
  /**
   * Show an error message
   * @param message Error message to display
   * @param autoClose Whether to automatically close the error after a duration
   * @param duration Duration in milliseconds before auto-closing (default: 5000ms)
   */
  showError(message: string | null, autoClose: boolean = false, duration: number = 5000): string {
    return this.addMessage(message, 'error', autoClose, duration);
  }
  
  /**
   * Show a warning message
   * @param message Warning message to display
   * @param autoClose Whether to automatically close the warning after a duration
   * @param duration Duration in milliseconds before auto-closing (default: 5000ms)
   */
  showWarning(message: string, autoClose: boolean = false, duration: number = 5000): string {
    return this.addMessage(message, 'warning', autoClose, duration);
  }
  
  /**
   * Show an info message
   * @param message Info message to display
   * @param autoClose Whether to automatically close the info after a duration
   * @param duration Duration in milliseconds before auto-closing (default: 3000ms)
   */
  showInfo(message: string, autoClose: boolean = true, duration: number = 3000): string {
    return this.addMessage(message, 'info', autoClose, duration);
  }
  
  /**
   * Clear a specific error by ID
   * @param id ID of the error to clear
   */
  clearError(id: string): void {
    const currentErrors = this.errorsSubject.getValue();
    this.errorsSubject.next(currentErrors.filter(error => error.id !== id));
  }
  
  /**
   * Clear all errors
   */
  clearAllErrors(): void {
    this.errorsSubject.next([]);
  }
  
  /**
   * Handle HTTP error responses
   * @param error HTTP error object
   * @param defaultMessage Default message to show if error doesn't have a message
   */
  handleHttpError(error: any, defaultMessage: string = 'An error occurred'): string {
    let errorMessage = defaultMessage;
    
    if (error) {
      // Try to extract error message from different possible formats
      if (error.error?.message) {
        errorMessage = error.error.message;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (typeof error.error === 'string') {
        errorMessage = error.error;
      } else if (error.statusText) {
        errorMessage = `${error.status}: ${error.statusText}`;
      }
    }
    
    return this.showError(errorMessage);
  }
  
  private addMessage(message: string | null, type: 'error' | 'warning' | 'info', autoClose: boolean, duration: number): string {
    const id = this.generateId();
    const errorMessage: ErrorMessage = {
      message,
      type,
      timestamp: new Date(),
      id,
      autoClose,
      duration
    };
    
    const currentErrors = this.errorsSubject.getValue();
    this.errorsSubject.next([...currentErrors, errorMessage]);
    
    if (autoClose) {
      setTimeout(() => {
        this.clearError(id);
      }, duration);
    }
    
    return id;
  }
  
  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }
}
